import React, { useState, useEffect } from 'react';
import { Card, Avatar, Typography, Space, Badge, Tooltip, List, Divider, Button } from 'antd';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import pusherService from '../services/pusherService';

const { Title, Text } = Typography;

const OnlineUsers = ({ onlineUsers, isConnected, currentUser, auctionOwnerId }) => {
    console.log('OnlineUsers component received props:', { 
        onlineUsersCount: onlineUsers?.length, 
        isConnected, 
        currentUser: currentUser?.id, 
        auctionOwnerId 
    });

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : '';
        const last = lastName ? lastName.charAt(0).toUpperCase() : '';
        return first + last;
    };

    const getDisplayName = (user) => {
        // If we have both first and last name, use them
        if (user.first_name && user.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        
        // If we only have first name, use it
        if (user.first_name) {
            return user.first_name;
        }
        
        // If we only have last name, use it
        if (user.last_name) {
            return user.last_name;
        }
        
        // If we have email, extract the name part before @
        if (user.email) {
            const emailName = user.email.split('@')[0];
            // Capitalize first letter and replace dots/underscores with spaces
            return emailName
                .replace(/[._]/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        }
        
        // Fallback to user ID if available
        if (user.id) {
            return `User ${user.id}`;
        }
        
        // Last resort
        return 'Guest User';
    };

    const isCurrentUser = (user) => {
        return currentUser && user.id === currentUser.id;
    };

    const isAuctionOwner = (user) => {
        return auctionOwnerId && user.id === auctionOwnerId;
    };

    const filterOutAuctionOwner = (users) => {
        // Always filter out the auction owner from the Connected Users list
        const filtered = users.filter(user => {
            const isOwner = isAuctionOwner(user);
            if (isOwner) {
                console.log('Filtering out auction owner:', user);
            }
            return !isOwner;
        });
        console.log(`Filtered ${users.length} users down to ${filtered.length} (removed ${users.length - filtered.length} auction owners)`);
        return filtered;
    };

    // Ensure we always show the correct count (excluding auction owner)
    const displayUsers = filterOutAuctionOwner(onlineUsers);
    const userCount = displayUsers.length;

    return (
        <Card 
            title={
                <Space>
                    <EyeOutlined />
                    <span>Connected Users</span>
                    <Badge 
                        count={userCount} 
                        style={{ backgroundColor: isConnected ? '#52c41a' : '#ff4d4f' }}
                    />
                </Space>
            }
            size="small"
            style={{ 
                height: '100%',
                minHeight: '200px'
            }}
            bodyStyle={{ 
                padding: '12px',
                height: 'calc(100% - 57px)',
                overflow: 'hidden'
            }}
        >
            <div style={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                gap: '8px'
            }}>
                {/* Connection Status */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: isConnected ? '#52c41a' : '#ff4d4f'
                    }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </Text>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* Connected Users List */}
                <div style={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    maxHeight: '300px'
                }}>
                     {userCount === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '20px 0',
                            color: '#8c8c8c'
                        }}>
                            <UserOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                            <div>No users connected</div>
                        </div>
                    ) : (
                        <List
                            dataSource={displayUsers}
                            renderItem={(user) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <List.Item.Meta
                                        avatar={
                                            <Tooltip title={getDisplayName(user)}>
                                                <Avatar 
                                                    size="small"
                                                    style={{ 
                                                        backgroundColor: isCurrentUser(user) ? '#1890ff' : '#87d068',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {getInitials(user.first_name, user.last_name)}
                                                </Avatar>
                                            </Tooltip>
                                        }
                                        title={
                                            <Text 
                                                style={{ 
                                                    fontSize: '12px',
                                                    fontWeight: isCurrentUser(user) ? 'bold' : 'normal',
                                                    color: isCurrentUser(user) ? '#1890ff' : 'inherit'
                                                }}
                                            >
                                                {getDisplayName(user)}
                                                {isCurrentUser(user) && ' (You)'}
                                            </Text>
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                                {user.email}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
};

export default OnlineUsers;
